import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AsyncData, TripTypes } from '@model/common';
import {
  MediaPost,
  MediaPostBaseRequestPayload,
  MediaPostErrorDetails,
  MediaPostRequestPayload
} from '@model/agents/agents-social-posts-models';
import { MediaPostApi } from '@model/iceberg/service/trade/MediaPostApi';

export enum MediaPostActions {
  SLICE_NAME = '@SOCIAL_MEDIA_POSTS',
  FETCH_POST = '@SOCIAL_MEDIA_POSTS/FETCH_MEDIA_POST',
  FETCH_TRIP_TYPE = '@SOCIAL_MEDIA_POSTS/FETCH_MEDIA_POST_TRIP_TYPE'
}

export interface MediaPostState {
  posts: AsyncData<Array<MediaPost>>;
  tripTypes: AsyncData<Array<TripTypes>>;
}

export const InitialMediaPostItem = {
  data: [],
  loading: false,
  error: null
};

export const INITIAL_MEDIA_POST_STATE: MediaPostState = {
  posts: InitialMediaPostItem,
  tripTypes: InitialMediaPostItem
};

const handleApiCall = async <T>(
  apiCall: () => Promise<T>,
  rejectWithValue: (value: MediaPostErrorDetails) => any,
  errorMessage: string
): Promise<T | any> => {
  try {
    const response: any = await apiCall();
    return response.data;
  } catch (error: any) {
    const status = error.response?.status || 500;
    const message = error.message || errorMessage;
    return rejectWithValue({ status, message });
  }
};

export const fetchSocialMediaPosts = createAsyncThunk<
  Array<MediaPost>,
  MediaPostRequestPayload,
  { rejectValue: MediaPostErrorDetails }
>(MediaPostActions.FETCH_POST, async (payload: MediaPostRequestPayload, { rejectWithValue }) => {
  const api = new MediaPostApi();
  return handleApiCall(() => api.getPostList(payload), rejectWithValue, 'Failed to fetch media posts');
});

export const fetchSocialMediaPostTripType = createAsyncThunk<
  Array<TripTypes>,
  MediaPostBaseRequestPayload,
  { rejectValue: MediaPostErrorDetails }
>(MediaPostActions.FETCH_TRIP_TYPE, async (payload: MediaPostBaseRequestPayload, { rejectWithValue }) => {
  const api = new MediaPostApi();
  return handleApiCall(() => api.getTripTypes(payload), rejectWithValue, 'Failed to fetch trip types');
});

const mediaPostSlice = createSlice({
  name: MediaPostActions.SLICE_NAME,
  initialState: INITIAL_MEDIA_POST_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocialMediaPosts.pending, (state) => {
        state.posts.loading = true;
      })
      .addCase(fetchSocialMediaPosts.fulfilled, (state, action) => {
        state.posts.loading = false;
        state.posts.data = action.payload;
        state.posts.error = null;
      })
      .addCase(fetchSocialMediaPosts.rejected, (state, action) => {
        state.posts.loading = false;
        state.posts.data = [];
        state.posts.error = action.payload;
      })
      .addCase(fetchSocialMediaPostTripType.pending, (state) => {
        state.tripTypes.loading = true;
      })
      .addCase(fetchSocialMediaPostTripType.fulfilled, (state, action) => {
        state.tripTypes.loading = false;
        state.tripTypes.data = action.payload;
        state.tripTypes.error = null;
      })
      .addCase(fetchSocialMediaPostTripType.rejected, (state, action) => {
        state.tripTypes.loading = false;
        state.tripTypes.data = [];
        state.tripTypes.error = action.payload || null;
      });
  }
});

export const mediaPostActions = { fetchSocialMediaPosts, fetchSocialMediaPostTripType };
export const mediaPostReducer = mediaPostSlice.reducer;
